import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import olreadiaService from "services/olreadiaService";
import { useAuth } from "contexts/AuthContext";
import { formatAnswerOptions, formatText, toMessage } from "./utils";
import { toast } from "react-toastify";
import SampleQuestionDrawer from "./SampleQuestionDrawer";
import SampleQuestionDetailsDrawer from "./SampleQuestionDetailsDrawer";

export default function SampleQuestionsPage() {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchPage = useCallback(async (pag = pagination) => {
    setLoading(true);
    setError(null);
    try {
      const res = await olreadiaService.sampleQuestions.list({
        page: pag.current - 1,
        size: pag.pageSize,
      });
      const content = Array.isArray(res?.content) ? res.content : [];
      setItems(content);
      setPagination((prev) => ({
        ...prev,
        current: pag.current,
        pageSize: pag.pageSize,
        total: res?.totalElements ?? content.length,
      }));
    } catch (err) {
      setError(toMessage(err, "Failed to load sample questions"));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current]);

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        fetchPage({ current: 1, pageSize: pagination.pageSize, search: query.trim() });
      } else {
        fetchPage({ current: 1, pageSize: pagination.pageSize });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, pagination.pageSize, fetchPage]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((item) => {
      const options = formatAnswerOptions(item?.answerOptions).join(", ");
      return [
        item?.question,
        item?.answerType,
        item?.answerValueType,
        item?.answerMetric,
        options,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [items, query]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await olreadiaService.sampleQuestions.update(editing.id, values, actorId);
        toast.success("Sample question updated");
      } else {
        await olreadiaService.sampleQuestions.create(values, actorId);
        toast.success("Sample question created");
      }
      setDrawerOpen(false);
      await fetchPage();
    } catch (err) {
      toast.error(toMessage(err, "Failed to save sample question"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Remove sample question?",
      content: "This will permanently delete the sample question.",
      okText: "Remove",
      okButtonProps: { danger: true, loading: deleting },
      onOk: async () => {
        setDeleting(true);
        try {
          await olreadiaService.sampleQuestions.delete(record.id, actorId);
          await fetchPage();
          toast.success("Sample question removed");
        } catch (err) {
          toast.error(toMessage(err, "Failed to delete sample question"));
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      ellipsis: true,
      render: (value) => {
        const text = value || "-";
        return (
          <Tooltip title={text === "-" ? "" : text} placement="topLeft">
            <span style={{ fontWeight: 600 }}>{formatText(text, 50)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Answer Type",
      dataIndex: "answerType",
      key: "answerType",
      width: 160,
      render: (value) => <Tag color="green">{value || "-"}</Tag>,
    },
    {
      title: "Value Type",
      dataIndex: "answerValueType",
      key: "answerValueType",
      width: 160,
      render: (value) => <Tag color="blue">{value || "-"}</Tag>,
    },
    {
      title: "Answer Options",
      key: "answerOptions",
      ellipsis: true,
      render: (_, record) => {
        const options = formatAnswerOptions(record?.answerOptions);
        const text = options.length ? options.join(", ") : "-";
        return (
          <Tooltip title={text === "-" ? "" : text} placement="topLeft">
            <span>{formatText(text, 48)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="prof-jobs-actions" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "view",
                  label: "View",
                  icon: <EyeOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setSelected(record);
                    setDetailsOpen(true);
                  },
                },
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    setEditing(record);
                    setDrawerOpen(true);
                  },
                },
                {
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleDelete(record);
                  },
                },
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="clipboard-list"
          title="Sample Questions"
          subtitle="Manage reusable sample interview questions."
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchPage()} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            New Question
          </Button>
        </Space>
      </div>

      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
        <Input.Search
          allowClear
          placeholder="Search questions, answer types, or metrics"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", maxWidth: 320 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={<Button size="small" onClick={() => fetchPage()}>Retry</Button>}
        />
      ) : null}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredItems}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={(pag) => fetchPage({ current: pag.current, pageSize: pag.pageSize })}
        locale={{ emptyText: "No sample questions found." }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".prof-jobs-actions")) return;
            setSelected(record);
            setDetailsOpen(true);
          },
        })}
      />

      <SampleQuestionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={saving}
        initialValues={editing}
      />

      <SampleQuestionDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        question={selected}
        onEdit={(record) => {
          setDetailsOpen(false);
          setEditing(record);
          setDrawerOpen(true);
        }}
      />
    </div>
  );
}
